/**
 * TrendingPanel — real-time "what's hot in this niche RIGHT NOW" widget
 * powered by Perplexity Sonar (web-searched, cited).
 *
 * Auto-fires with the primary_niche returned by the niche review, so the user
 * gets a niche diagnosis + trending topics in the SAME workflow. Silently
 * hides itself if the backend returns `not_configured` (Perplexity key not set).
 */
import React, { useState, useEffect } from "react";
import axios from "axios";
import { Radio, ExternalLink, Loader2 } from "lucide-react";
import { Badge } from "./components/ui/badge";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function TrendingPanel({ niche }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notConfigured, setNotConfigured] = useState(false);

  useEffect(() => {
    if (!niche) return;
    let cancelled = false;
    setLoading(true);
    setNotConfigured(false);
    setData(null);
    axios
      .post(`${API}/competitors/trending`, null, { params: { niche } })
      .then(({ data }) => { if (!cancelled) setData(data); })
      .catch((e) => {
        if (cancelled) return;
        const detail = e?.response?.data?.detail;
        if (e?.response?.status === 503 && typeof detail === "object" && detail?.code === "not_configured") {
          // Silent hide — Perplexity key not set yet
          setNotConfigured(true);
        }
        // Other errors: global axios interceptor handles 429 cap-hit; other
        // failures we silently drop (panel is auxiliary — niche review still shows).
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [niche]);

  if (notConfigured) return null;
  if (!loading && !data) return null;

  return (
    <div
      className="mb-6 rounded-2xl border border-white/[0.08] bg-white/[0.02] p-6"
      data-testid="trending-panel"
    >
      <div className="flex items-center gap-2 mb-4">
        <Radio className={`w-4 h-4 ${loading ? "text-amber-400 animate-pulse" : "text-emerald-400"}`} />
        <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold">
          Trending in this niche right now
        </span>
        {data?.model && (
          <span className="text-[10px] text-zinc-600 font-mono ml-auto">via Perplexity {data.model}</span>
        )}
      </div>

      {loading && (
        <div className="flex items-center gap-3 text-sm text-zinc-400" data-testid="trending-panel-loading">
          <Loader2 className="w-4 h-4 animate-spin text-amber-400" />
          Searching the live web for what&rsquo;s trending in this niche&hellip;
        </div>
      )}

      {data?.topics?.length > 0 && (
        <div className="space-y-3" data-testid="trending-panel-topics">
          {data.topics.map((t, i) => (
            <div
              key={`${t.title}-${i}`}
              className="rounded-xl bg-black/30 border border-white/[0.06] p-4"
              data-testid={`trending-topic-${i}`}
            >
              <div className="flex items-start gap-3">
                <Badge className="bg-emerald-500/15 text-emerald-300 border border-emerald-500/25 hover:bg-emerald-500/15 flex-shrink-0">
                  #{i + 1}
                </Badge>
                <div className="flex-1 min-w-0">
                  <h4 className="text-white font-medium text-sm mb-1.5 leading-snug">{t.title}</h4>
                  {t.why_now && (
                    <p className="text-xs text-zinc-400 mb-1.5 leading-relaxed">
                      <span className="text-amber-300 font-medium">Why now:</span> {t.why_now}
                    </p>
                  )}
                  {t.audience_angle && (
                    <p className="text-xs text-zinc-400 leading-relaxed">
                      <span className="text-emerald-300 font-medium">Audience angle:</span> {t.audience_angle}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {data?.citations?.length > 0 && (
        <div className="mt-4 pt-3 border-t border-white/5" data-testid="trending-panel-citations">
          <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-2">Sources</div>
          <ul className="space-y-1">
            {data.citations.slice(0, 8).map((url) => (
              <li key={url}>
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs text-emerald-400 hover:text-emerald-300 truncate max-w-full"
                >
                  <ExternalLink className="w-3 h-3 flex-shrink-0" />
                  <span className="truncate">{url}</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
